import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  LogIn,
  LogOut,
  Coffee,
  Play,
  Pencil,
  Trash2,
  AlertTriangle,
  MapPinOff,
  Clock,
  PlayCircle,
  StopCircle,
  UserX,
  Calendar,
  CalendarOff,
  X,
  ExternalLink,
  MapPin,
  Smartphone,
  type LucideIcon,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/core/utils';
import type { TimeTrackingActivityEventWithEmployee } from '@/types/time-tracking-activity';
import { ACTIVITY_EVENT_CONFIG } from '@/types/time-tracking-activity';

interface TimeTrackingLogDetailPanelProps {
  event: TimeTrackingActivityEventWithEmployee;
  onClose: () => void;
  onShiftClick?: (shiftId: string, shiftDate: Date) => void;
}

const ICON_MAP: Record<string, LucideIcon> = {
  LogIn,
  LogOut,
  Coffee,
  Play,
  Pencil,
  Trash2,
  AlertTriangle,
  MapPinOff,
  Clock,
  PlayCircle,
  StopCircle,
  UserX,
  Calendar,
  CalendarOff,
};

export const TimeTrackingLogDetailPanel: React.FC<TimeTrackingLogDetailPanelProps> = ({
  event,
  onClose,
  onShiftClick,
}) => {
  const config = ACTIVITY_EVENT_CONFIG[event.event_type];
  const Icon = ICON_MAP[config.icon] || Clock;
  const eventTime = new Date(event.event_timestamp);

  const isUnassignedShift = event.event_type === 'unassigned_shift_started' || event.event_type === 'unassigned_shift_ended';

  // Target employee (whose time is being tracked)
  const employeeInitials = event.employee?.name
    ? event.employee.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : isUnassignedShift ? '--' : '??';

  // Actor (who made the change) - for manual edits
  const actorEmployee = event.actor_employee;
  const actorInitials = actorEmployee?.name
    ? actorEmployee.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : null;

  const hasShift = !!event.shift_id;
  const shiftDate = event.metadata?.shift_start_time
    ? new Date(event.metadata.shift_start_time)
    : eventTime;

  const handleShiftClick = () => {
    if (hasShift && onShiftClick && event.shift_id) {
      onShiftClick(event.shift_id, shiftDate);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <h3 className="text-sm font-medium">Event Details</h3>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Event Type Header */}
        <div className="flex items-center gap-2">
          <Icon className={cn('h-5 w-5', config.color)} />
          <span className="font-medium">{config.label}</span>
          {event.metadata?.source === 'dotypos' && (
            <Badge variant="outline" className="text-xs">
              Dotypos
            </Badge>
          )}
        </div>

        {/* Changed by Section - who recorded/made the change */}
        <div className="p-3 rounded-lg bg-muted/50 space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Changed by</p>
          <div className="flex items-center gap-2">
            {actorEmployee ? (
              <>
                <Avatar className="h-6 w-6">
                  <AvatarImage src={actorEmployee.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">{actorInitials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{actorEmployee.name}</span>
              </>
            ) : event.employee ? (
              <>
                <Avatar className="h-6 w-6">
                  <AvatarImage src={event.employee.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">{employeeInitials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{event.employee.name}</span>
              </>
            ) : (
              <>
                <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                </div>
                <span className="text-sm text-muted-foreground">System</span>
              </>
            )}
          </div>
        </div>

        {/* Affected Employee Section - who was affected (if different from actor) */}
        {event.employee && actorEmployee && event.employee.id !== actorEmployee.id && (
          <div className="p-3 rounded-lg bg-muted/50 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Affected Employee</p>
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarImage src={event.employee.avatar_url || undefined} />
                <AvatarFallback className="text-xs">{employeeInitials}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{event.employee.name}</span>
            </div>
          </div>
        )}

        {/* Unassigned shift indicator */}
        {isUnassignedShift && (
          <div className="p-3 rounded-lg bg-muted/50 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Employee</p>
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                <Clock className="h-3 w-3 text-muted-foreground" />
              </div>
              <span className="text-sm text-muted-foreground">Unassigned</span>
            </div>
          </div>
        )}

        <Separator />

        {/* Timestamp */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</p>
          <p className="text-sm">
            {format(eventTime, 'EEEE, MMMM d, yyyy')}
          </p>
          <p className="text-lg font-semibold tabular-nums">
            {format(eventTime, 'h:mm:ss a')}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDistanceToNow(eventTime, { addSuffix: true })}
          </p>
        </div>

        {/* Metadata Section */}
        {(event.metadata?.auth_method || event.metadata?.source || event.metadata?.location || event.metadata?.device_id) && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Details</p>

              {event.metadata?.auth_method && (
                <div className="flex items-center gap-2 text-sm">
                  <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Method: <span className="capitalize">{event.metadata.auth_method}</span></span>
                </div>
              )}

              {event.metadata?.source && event.metadata.source !== 'internal' && (
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="text-xs">
                    {event.metadata.source}
                  </Badge>
                </div>
              )}

              {event.metadata?.device_id && (
                <div className="flex items-center gap-2 text-sm">
                  <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground truncate">Device: {event.metadata.device_id}</span>
                </div>
              )}

              {event.metadata?.location && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>Location verified</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Event-specific details */}
        {event.event_type === 'manual_edit' && event.metadata && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Changes</p>
              {event.metadata.previous_time && event.metadata.new_time && (
                <div className="text-sm">
                  <span className="text-muted-foreground line-through">
                    {format(new Date(event.metadata.previous_time), 'h:mm a')}
                  </span>
                  <span className="mx-2">→</span>
                  <span className="font-medium">
                    {format(new Date(event.metadata.new_time), 'h:mm a')}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {event.event_type === 'geofence_violation' && event.metadata && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Violation Details</p>
              <div className="p-2 rounded bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
                <p>Distance: {event.metadata.distance_meters}m from allowed area</p>
                <p className="text-xs text-red-600 dark:text-red-400">
                  Allowed radius: {event.metadata.allowed_radius}m
                </p>
              </div>
            </div>
          </>
        )}

        {event.event_type === 'overtime_alert' && event.metadata && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Overtime Info</p>
              <div className="p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-300">
                <p>Hours worked: {event.metadata.hours_worked?.toFixed(1)}h</p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Threshold: {event.metadata.threshold_hours}h
                </p>
              </div>
            </div>
          </>
        )}

        {(event.event_type === 'shift_started' || event.event_type === 'shift_ended') && event.metadata && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Shift</p>
              <button
                onClick={handleShiftClick}
                disabled={!hasShift || !onShiftClick}
                className={cn(
                  'w-full text-left p-2 rounded-lg transition-colors',
                  hasShift && onShiftClick
                    ? 'hover:bg-primary/10 cursor-pointer group'
                    : 'cursor-default'
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    {event.metadata.shift_title && (
                      <p className="text-sm font-medium">{event.metadata.shift_title}</p>
                    )}
                    {event.metadata.shift_start_time && event.metadata.shift_end_time && (
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(event.metadata.shift_start_time), 'h:mm a')} - {format(new Date(event.metadata.shift_end_time), 'h:mm a')}
                      </p>
                    )}
                  </div>
                  {hasShift && onShiftClick && (
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  )}
                </div>
              </button>
            </div>
          </>
        )}

        {event.event_type === 'employee_no_show' && event.metadata && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">No Show Details</p>
              <div className="p-2 rounded bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
                {event.metadata.shift_start_time && (
                  <p>Scheduled: {format(new Date(event.metadata.shift_start_time), 'h:mm a')}</p>
                )}
                {event.metadata.grace_minutes && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {event.metadata.grace_minutes} min grace period elapsed
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {(event.event_type === 'unassigned_shift_started' || event.event_type === 'unassigned_shift_ended') && event.metadata && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Unassigned Shift</p>
              <button
                onClick={handleShiftClick}
                disabled={!hasShift || !onShiftClick}
                className={cn(
                  'w-full text-left p-2 rounded-lg transition-colors',
                  hasShift && onShiftClick
                    ? 'hover:bg-primary/10 cursor-pointer group'
                    : 'cursor-default'
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    {event.metadata.shift_title && (
                      <p className="text-sm font-medium text-purple-600 dark:text-purple-400">{event.metadata.shift_title}</p>
                    )}
                    {event.metadata.shift_start_time && event.metadata.shift_end_time && (
                      <p className="text-sm text-purple-600 dark:text-purple-400">
                        {format(new Date(event.metadata.shift_start_time), 'h:mm a')} - {format(new Date(event.metadata.shift_end_time), 'h:mm a')}
                      </p>
                    )}
                  </div>
                  {hasShift && onShiftClick && (
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  )}
                </div>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Footer Action */}
      {hasShift && onShiftClick && (
        <div className="p-3 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleShiftClick}
          >
            <ExternalLink className="h-3.5 w-3.5 mr-2" />
            View Shift
          </Button>
        </div>
      )}
    </div>
  );
};

export default TimeTrackingLogDetailPanel;
